import { DateTime } from 'luxon';
import { isEmpty } from 'lodash';
import { isString } from 'class-validator';

import { toDateTime } from './chronon';
import type { DateLike } from './nova-date';
import {
  makeCompanySettings,
  type CompanySettingsInput,
  type DateDisplayFormat,
  type ISettings,
  type TimeDisplayFormat
} from './settings';

/** Format pattern strings for each company {@link DateDisplayFormat} setting value. */
export const COMPANY_DATE_FORMAT_PATTERNS: Record<DateDisplayFormat, string> = {
  MDY: 'MM/dd/yyyy',
  DMY: 'dd/MM/yyyy',
  YMD: 'yyyy-MM-dd',
  weekdayMDY: 'EEE, MM/dd/yyyy',
  weekdayDMY: 'EEE, dd/MM/yyyy',
  weekdayYMD: 'EEE, yyyy-MM-dd'
};

/** Format pattern strings for each company {@link TimeDisplayFormat} setting value. */
export const COMPANY_TIME_FORMAT_PATTERNS: Record<TimeDisplayFormat, string> = {
  twelveHour: 'h:mm a',
  twentyFourHour: 'HH:mm'
};

export interface CompanyDisplayFormatOptions {
  /** Carrier company (or any object with optional `settings`). Defaults apply when null or partial. */
  company?: CompanySettingsInput;
  /** Warehouse IANA timezone — determines the local calendar date/time for the instant. */
  timezone?: string;
  /**
   * Locale for weekday abbreviations in date formats that include `EEE`.
   * Does not change numeric date order (controlled by company settings).
   */
  locale?: string;
}

type ResolvedCompanyDisplayFormats = {
  dateDisplayFormat: DateDisplayFormat;
  timeDisplayFormat: TimeDisplayFormat;
  dateFormatPattern: string;
  timeFormatPattern: string;
  dateTimeFormatPattern: string;
};

/**
 * Resolves effective company display settings and their format pattern strings.
 * Uses {@link makeCompanySettings} for US defaults when settings are null or partial.
 */
export function resolveCompanyDisplayFormats(
  company?: CompanySettingsInput
): ResolvedCompanyDisplayFormats {
  const settings = makeCompanySettings(company);
  return buildFormatsFromSettings(settings);
}

function buildFormatsFromSettings(settings: ISettings): ResolvedCompanyDisplayFormats {
  const dateDisplayFormat = settings.dateDisplayFormat as DateDisplayFormat;
  const timeDisplayFormat = settings.timeDisplayFormat as TimeDisplayFormat;
  const dateFormatPattern = COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat];
  const timeFormatPattern = COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat];

  return {
    dateDisplayFormat,
    timeDisplayFormat,
    dateFormatPattern,
    timeFormatPattern,
    dateTimeFormatPattern: `${dateFormatPattern} ${timeFormatPattern}`
  };
}

export function getCompanyDateFormatPattern(dateDisplayFormat: DateDisplayFormat): string {
  return COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat];
}

export function getCompanyTimeFormatPattern(timeDisplayFormat: TimeDisplayFormat): string {
  return COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat];
}

export function getCompanyDateTimeFormatPattern(
  dateDisplayFormat: DateDisplayFormat,
  timeDisplayFormat: TimeDisplayFormat
): string {
  return `${COMPANY_DATE_FORMAT_PATTERNS[dateDisplayFormat]} ${COMPANY_TIME_FORMAT_PATTERNS[timeDisplayFormat]}`;
}

function resolveFormats(options: CompanyDisplayFormatOptions): ResolvedCompanyDisplayFormats {
  return buildFormatsFromSettings(makeCompanySettings(options.company));
}

function toLocalizedDateTime(input: DateLike, timezone?: string): DateTime {
  return toDateTime(
    input,
    isString(timezone) && !isEmpty(timezone) ? { zone: timezone } : undefined
  );
}

function formatWithLocale(dateTime: DateTime, format: string, locale?: string): string {
  return !isEmpty(locale) ? dateTime.toFormat(format, { locale }) : dateTime.toFormat(format);
}

function buildParseFormatOptions(
  options: Pick<CompanyDisplayFormatOptions, 'timezone' | 'locale'>
): { zone?: string; locale?: string } {
  return {
    ...(isString(options.timezone) && !isEmpty(options.timezone) ? { zone: options.timezone } : {}),
    ...(isString(options.locale) && !isEmpty(options.locale) ? { locale: options.locale } : {})
  };
}

/** Formats an instant as a date string using company display preferences. */
export function formatCompanyDate(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { dateFormatPattern } = resolveFormats(options);
  const dateTime = toLocalizedDateTime(input, options.timezone);
  return formatWithLocale(dateTime, dateFormatPattern, options.locale);
}

/** Formats an instant as a time string using company display preferences. */
export function formatCompanyTime(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { timeFormatPattern } = resolveFormats(options);
  const dateTime = toLocalizedDateTime(input, options.timezone);
  return formatWithLocale(dateTime, timeFormatPattern, options.locale);
}

/** Formats an instant as a combined date and time string using company display preferences. */
export function formatCompanyDateTime(
  input: DateLike,
  options: CompanyDisplayFormatOptions = {}
): string {
  const { dateTimeFormatPattern } = resolveFormats(options);
  const dateTime = toLocalizedDateTime(input, options.timezone);
  return formatWithLocale(dateTime, dateTimeFormatPattern, options.locale);
}

/** Parses a date string that was formatted with {@link formatCompanyDate} (same pattern). */
export function parseCompanyDate(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { dateFormatPattern } = resolveFormats(options);
  const parsed = DateTime.fromFormat(
    value.trim(),
    dateFormatPattern,
    buildParseFormatOptions(options)
  );
  return parsed.isValid ? parsed : null;
}

/** Parses a time string that was formatted with {@link formatCompanyTime} (same pattern). */
export function parseCompanyTime(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { timeFormatPattern } = resolveFormats(options);
  const parsed = DateTime.fromFormat(
    value.trim(),
    timeFormatPattern,
    buildParseFormatOptions(options)
  );
  return parsed.isValid ? parsed : null;
}

/** Parses a combined date/time string formatted with {@link formatCompanyDateTime}. */
export function parseCompanyDateTime(
  value: string,
  options: CompanyDisplayFormatOptions = {}
): DateTime | null {
  const { dateTimeFormatPattern } = resolveFormats(options);
  const parsed = DateTime.fromFormat(
    value.trim(),
    dateTimeFormatPattern,
    buildParseFormatOptions(options)
  );
  return parsed.isValid ? parsed : null;
}
